//! Renderizza il blocco `evidence` del proof JSON di un attempt UMA.
//!
//! Il proof JSON contiene `evidence` con campi che variano per source:
//!  - `manual`     → evidence_url, evidence_note
//!  - `sportradar` → match_id, sport, raw response summary
//!  - `chainlink`  → feed_address, latest_price, threshold, comparator
//!
//! Componente totalmente parsing-tolerante: se evidence è assente o malformato
//! non renderizza nulla (ritorna null).

type EvidenceObject = Record<string, unknown>;

export function ProviderEvidence({ proof }: { proof: unknown }) {
  if (!proof || typeof proof !== "object") return null;
  const obj = proof as Record<string, unknown>;
  const evidence = obj["evidence"];
  if (!evidence || typeof evidence !== "object") return null;
  const ev = evidence as EvidenceObject;
  const source = typeof ev["source"] === "string" ? (ev["source"] as string) : "unknown";

  return (
    <div className="rounded-card border border-line bg-bg-2 p-3">
      <div className="mb-2 text-[11px] uppercase tracking-wider text-muted">
        Evidence (
        <span className="font-mono text-text-2">{source}</span>)
      </div>
      {source === "manual" && <ManualEvidence ev={ev} />}
      {source === "sportradar" && <SportradarEvidence ev={ev} />}
      {source === "chainlink" && <ChainlinkEvidence ev={ev} />}
      {source !== "manual" && source !== "sportradar" && source !== "chainlink" && (
        <UnknownEvidence ev={ev} />
      )}
    </div>
  );
}

// ─── per-source renderers ────────────────────────────────────────────

function ManualEvidence({ ev }: { ev: EvidenceObject }) {
  const url = stringField(ev, "evidence_url");
  const note = stringField(ev, "evidence_note");
  const proposedAt = stringField(ev, "proposed_at");
  return (
    <div className="flex flex-col gap-2 text-[12.5px]">
      {url && (
        <div>
          <span className="text-muted">Fonte: </span>
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="break-all text-accent hover:underline"
          >
            {url} <span aria-hidden>↗</span>
          </a>
        </div>
      )}
      {note && (
        <div>
          <span className="text-muted">Nota: </span>
          <span className="text-text-2">{note}</span>
        </div>
      )}
      {proposedAt && (
        <div className="text-[11px] text-muted">
          Inserito il {new Date(proposedAt).toLocaleString()}
        </div>
      )}
    </div>
  );
}

function SportradarEvidence({ ev }: { ev: EvidenceObject }) {
  const matchId = stringField(ev, "match_id");
  const sport = stringField(ev, "sport");
  const final = stringField(ev, "final_score") ?? stringField(ev, "result");
  return (
    <div className="flex flex-col gap-2 text-[12.5px]">
      {sport && (
        <div>
          <span className="text-muted">Sport: </span>
          <span className="text-text-2">{sport}</span>
        </div>
      )}
      {matchId && (
        <div>
          <span className="text-muted">Match ID: </span>
          <span className="font-mono text-text-2">{matchId}</span>
        </div>
      )}
      {final && (
        <div>
          <span className="text-muted">Risultato: </span>
          <span className="font-mono font-semibold text-text">{final}</span>
        </div>
      )}
      <div className="text-[11px] text-muted">
        Fonte: Sportradar (signed attestation on-chain via bridge)
      </div>
    </div>
  );
}

function ChainlinkEvidence({ ev }: { ev: EvidenceObject }) {
  const feed = stringField(ev, "feed_address");
  const price = stringField(ev, "latest_price");
  const threshold = stringField(ev, "threshold");
  const comparator = stringField(ev, "comparator");
  return (
    <div className="flex flex-col gap-2 text-[12.5px]">
      {feed && (
        <div>
          <span className="text-muted">Feed: </span>
          <a
            href={`https://polygonscan.com/address/${feed}`}
            target="_blank"
            rel="noreferrer"
            className="break-all font-mono text-accent hover:underline"
          >
            {feed} <span aria-hidden>↗</span>
          </a>
        </div>
      )}
      {price && (
        <div>
          <span className="text-muted">Prezzo letto: </span>
          <span className="font-mono font-semibold text-text">{price}</span>
        </div>
      )}
      {threshold && comparator && (
        <div>
          <span className="text-muted">Condizione: </span>
          <span className="font-mono text-text-2">
            price {comparator} {threshold}
          </span>
        </div>
      )}
      <div className="text-[11px] text-muted">Fonte: Chainlink Data Feed</div>
    </div>
  );
}

function UnknownEvidence({ ev }: { ev: EvidenceObject }) {
  return (
    <pre className="max-h-[160px] overflow-auto font-mono text-[11.5px] leading-[1.4] text-text-2">
      {JSON.stringify(ev, null, 2)}
    </pre>
  );
}

// ─── helpers ─────────────────────────────────────────────────────────

function stringField(obj: EvidenceObject, key: string): string | undefined {
  const v = obj[key];
  return typeof v === "string" ? v : undefined;
}
